import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Check, ChevronRight } from "lucide-react";
import { track } from "@/lib/track";
import { SectionClouds } from "./SectionClouds";

type PlanId = "audit" | "core" | "premium";

interface PlanData {
  name: string;
  priceLead?: string;
  priceEm?: string;
  tagline: string;
  bullets: string[];
}

const planOrder: PlanId[] = ["audit", "core", "premium"];
const featuredId: PlanId = "core";
const badgedId: PlanId = "core";

function PlanPrice({ id, plan, perMonth }: { id: PlanId; plan: PlanData; perMonth: string }) {
  // Audit: "From €990" (EN) / "À partir de 990 €" (FR) — number is the
  // italic accent. `whitespace-nowrap` keeps the amount and its currency
  // symbol on the same line in French, where price formatting is
  // "<amount> €" with a space.
  if (id === "audit") {
    return (
      <>
        {plan.priceLead} <span className="serif-italic whitespace-nowrap">{plan.priceEm}</span>
      </>
    );
  }
  // Core: "Tailored" — single italic word.
  if (id === "core") {
    return <span className="serif-italic">{plan.priceEm}</span>;
  }
  // Premium: "Retainer / month" — small "/ month" suffix beside the word.
  return (
    <>
      {plan.priceLead}
      <span className="ml-1 align-baseline text-[0.4em] font-normal text-ink-3">{perMonth}</span>
    </>
  );
}

export function Pricing() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const perMonth = t("landing.pricing.perMonth");

  return (
    <section ref={ref} id="pricing" className="frame bg-surface-2/40">
      <SectionClouds variant="pricing" />
      <div className="container-narrow section-pad relative z-10">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">06 —</span> {t("landing.pricing.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.pricing.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.pricing.lede")}</p>
          <p className="mono mt-3 text-[12px] text-ink-3">{t("landing.pricing.freeCallNote")}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 items-stretch gap-5 md:grid-cols-3 lg:gap-6">
          {planOrder.map((id, i) => {
            const plan = t(`landing.pricing.plans.${id}`, {
              returnObjects: true,
            }) as PlanData;
            const featured = id === featuredId;
            const badged = id === badgedId;
            return (
              <div
                key={id}
                className={cn(
                  "relative flex flex-col rounded-[24px] p-8 sm:p-9",
                  "transition-all duration-700",
                  featured
                    ? "bg-ink text-page-bg shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)] md:-my-2 md:scale-[1.02]"
                    : "border border-line bg-surface text-ink",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
                )}
                style={{ transitionDelay: isVisible ? `${i * 80}ms` : "0ms" }}
              >
                {badged && (
                  <div className="absolute -top-3 right-6 inline-flex items-center rounded-full bg-accent px-3 py-1 text-[11px] font-medium tracking-tight text-white">
                    {t("landing.pricing.mostPopular")}
                  </div>
                )}

                <div className="mono text-[11px] tracking-[0.16em] text-accent uppercase">
                  {plan.name}
                </div>
                <div
                  className={cn(
                    "mt-4 text-[clamp(36px,3.6vw,52px)] leading-none font-medium tracking-[-0.025em]",
                    featured ? "text-page-bg" : "text-ink",
                  )}
                >
                  <PlanPrice id={id} plan={plan} perMonth={perMonth} />
                </div>
                <p
                  className={cn(
                    "mt-3 text-[14px] leading-[1.55]",
                    featured ? "text-page-bg/70" : "text-ink-2",
                  )}
                >
                  {plan.tagline}
                </p>

                <ul className="mt-6 flex-1 space-y-2.5">
                  {plan.bullets.map((b) => (
                    <li
                      key={b}
                      className={cn(
                        "flex items-start gap-2.5 text-[13.5px] leading-snug",
                        featured ? "text-page-bg/85" : "text-ink-2",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                          featured
                            ? "border-accent/60 text-accent"
                            : "border-accent/40 text-accent",
                        )}
                      >
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://www.cal.eu/andreacab/holymoly-free-call?user=andreacab"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("cta_click", { source: "pricing", plan: id })}
                  className={cn(
                    "mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full py-2 pr-2 pl-5 text-[14px] font-medium tracking-tight transition-colors",
                    featured
                      ? "bg-accent text-white hover:bg-accent-hover"
                      : "bg-ink text-page-bg hover:bg-[#000]",
                  )}
                >
                  {t("landing.pricing.getStarted")}
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                    <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
